import { useState, useCallback, useEffect, ReactNode } from "react";

import { createUseStyles } from "$utils/createUseStyles";
import { Accept, useDropzone } from "react-dropzone";
import { Text } from "$components";
import { publicIconPath } from "$utils/constants";
import DefaultFilesList from "./DefaultFilesList";
import useAuth from "$hooks/useAuth";
import useMedia from "$hooks/useMedia";
import { theme } from "$utils/theme/bataillog";

type Props = {
  value?: any;
  accept?: Accept;
  children?: ReactNode;
  onChange?: (values: any) => void;
  nbCols?: number;
  label?: string;
  maxSize?: number;
  orientation?: "horizontal" | "vertical";
  max?: number;
  name?: string;
  onUpload?: (value: any) => void;
  isAdmin?: boolean;
  isReadOnly?: boolean;
  isStrapi?: boolean;
  company?: string;
  type?: string;
  id?: string;
  isEdit?: boolean;
  isLargeVersion?: boolean;
  hideFileList?: boolean;
  isSplitted?: boolean;
  secondaryValue?: any;
  mainLabel?: string;
  mainEmptyLabel?: string;
  secondaryLabel?: string;
  secondaryEmptyLabel?: string;
};

function UploadFiles({
  value = null,
  accept,
  children: _children = null,
  onChange = () => {},
  nbCols = 3,
  label: _label = null,
  maxSize = 1024 * 1024 * 30,
  max = Infinity,
  orientation = "horizontal",
  name: _name = "",
  onUpload = null,
  isAdmin = false,
  isReadOnly = false,
  isStrapi = false,
  company,
  type,
  id,
  isEdit,
  isLargeVersion = false,
  hideFileList = false,
  isSplitted = false,
  secondaryValue = null,
  mainLabel = "",
  mainEmptyLabel = "",
  secondaryLabel = "",
  secondaryEmptyLabel = "",
  // "data-cy": dataCy,
  ...rest
}: Props) {
  const [files, setFiles] = useState([]);
  const [uploadUrl, setUploadUrl] = useState<string>(undefined);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const { token } = useAuth();
  const { deleteMedia } = useMedia(type);

  const classes = useStyles();

  useEffect(() => {
    if (isStrapi) {
      setUploadUrl(`${process.env.REACT_APP_ORDER_API_URL}/upload`);
    } else {
      setUploadUrl(`${process.env.REACT_APP_API_ENDPOINT}/api/media/upload`);
    }
  }, [isStrapi]);

  const onRemoveFile = useCallback(
    (removeFile: any) => async () => {
      const newFiles = files.filter((f: any) => {
        const fileData = f.attributes || f;

        if (fileData.hash && removeFile.hash) {
          return fileData.hash !== removeFile.hash;
        }

        if (f.id && removeFile.id) {
          return f.id !== removeFile.id;
        }

        if (fileData._id && removeFile._id) {
          return fileData._id !== removeFile._id;
        }

        if (
          fileData.name &&
          fileData.size &&
          removeFile.name &&
          removeFile.size
        ) {
          return !(
            fileData.name === removeFile.name &&
            fileData.size === removeFile.size
          );
        }

        if (fileData.index !== undefined && removeFile.index !== undefined) {
          return fileData.index !== removeFile.index;
        }

        return true;
      });

      if (!isStrapi && !isEdit && removeFile._id) {
        setFiles([
          ...newFiles,
          { ...removeFile, state: "pending", percentage: 50 },
        ]);
        await deleteMedia(removeFile._id);
      }
      onChange(newFiles);
    },
    [deleteMedia, files, setFiles, isEdit, isStrapi, onChange]
  );

  const sendFile = useCallback(
    async (file: any) =>
      new Promise((resolve) => {
        const req = new XMLHttpRequest();

        req.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            setUploadingFiles((prevFiles: any) => ({
              ...prevFiles,
              [file.name]: {
                ...file,
                state: "pending",
                percentage: (event.loaded / event.total) * 100,
              },
            }));
          }
        });

        req.upload.addEventListener("load", () => {
          setUploadingFiles((prevFiles: any) => ({
            ...prevFiles,
            [file.name]: {
              ...file,
              state: "done",
              percentage: 100,
            },
          }));
        });

        req.upload.addEventListener("error", () => {
          setUploadingFiles((prevFiles: any) => ({
            ...prevFiles,
            [file.name]: {
              ...prevFiles[file.name],
              state: "error",
              percentage: 0,
            },
          }));
          resolve(null);
        });

        req.addEventListener("load", function () {
          try {
            const response = JSON.parse(this.response);

            const res = Array.isArray(response) ? response[0] : response;

            if (onUpload) {
              onUpload(res);
            }

            resolve({
              ...res,
              state: "done",
              percentage: 100,
              filename: res.filename ?? res.name,
              name: res.name ?? res.filename,
            });
          } catch {
            resolve({
              ...file,
              state: "error",
              percentage: 0,
              filename: "titi",
            });
          }
        });

        const formData = new FormData();

        formData.append("files", file.file);
        req.onload = () => {
          // console.log(req.responseText);
        };
        req.open("POST", uploadUrl, true);
        if (!isStrapi) {
          req.setRequestHeader("Authorization", `Bearer ${token}`);
          formData.append("company", company);
          formData.append("type", type);
          if (isLargeVersion && type !== "shipping") {
            formData.append("linkedObjectId", id);
          }
        }
        req.send(formData);
      }),
    [uploadUrl, isStrapi, onUpload, token, company, type, isLargeVersion, id]
  );

  const handleFileSelected = useCallback(
    async (inputFiles: any) => {
      if (!inputFiles.length || files.length + inputFiles.length > max) {
        return;
      }
      const fileslist = [] as any[];
      inputFiles.forEach((inputFile: File) => {
        const maxIndex = Math.max(
          ...fileslist.map((currentFile) => currentFile.index)
        );
        const file: any = {
          index: fileslist.length ? maxIndex + 1 : 0,
          inputFile,
          name: inputFile.name,
          url: URL.createObjectURL(inputFile),
        };

        if (file.inputFile.size <= maxSize) {
          fileslist.push(file);
        }
      });

      const tmpFiles = fileslist;

      const filesToUpload = {} as any;

      tmpFiles.forEach((file) => {
        filesToUpload[file.name] = {
          name: file.name,
          url: file.url,
          file: file.inputFile,
          type: file.inputFile.type,
          state: "pending",
          percentage: 0,
        };
      });

      setUploadingFiles(filesToUpload);

      const promiseFiles = [] as any;

      Object.values(filesToUpload).forEach((f) => {
        promiseFiles.push(sendFile(f));
      });
      const uploadedFiles = await Promise.all(promiseFiles);

      const newFiles = [...files] as any;

      uploadedFiles.forEach((file) => {
        if (file) newFiles.push(file);
      });
      setFiles(newFiles);
      onChange(newFiles);

      setUploadingFiles({});
    },
    [files, max, maxSize, onChange, sendFile]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileSelected,
    multiple: true,
    accept,
  });

  const ufiles = Object.values(uploadingFiles) ?? [];
  const fileslist = [...files, ...ufiles];

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setFiles(value);
    } else {
      setFiles([]);
    }
  }, [value, setFiles]);

  if (isLargeVersion) {
    return (
      <div
        {...rest}
        className={
          orientation === "horizontal"
            ? classes.fileUpload
            : classes.fileUploadVertical
        }
      >
        {!hideFileList && (
          <>
            <DefaultFilesList
              axis="xy"
              nbCols={nbCols}
              items={fileslist}
              onRemoveFile={onRemoveFile}
              isAdmin={isAdmin}
              className={{ backgroundColor: "red" }}
              isSplitted={isSplitted}
              mainLabel={mainLabel}
              mainEmptyLabel={mainEmptyLabel}
              secondaryLabel={secondaryLabel}
              secondaryItems={secondaryValue}
              secondaryEmptyLabel={secondaryEmptyLabel}
            />
            <div className={classes.divider} />
          </>
        )}

        {!isReadOnly && (
          <div className={classes.largeDragContainer}>
            <div {...getRootProps({ className: classes.dropZone })}>
              <input {...getInputProps()} />
              <div>
                <img
                  alt="ico-download"
                  src={`${publicIconPath}/download.svg`}
                  className={classes.imgUpload}
                />
                <Text className={classes.textMain}>Déposez vos fichiers</Text>
              </div>
              <Text className={classes.textAdd}>
                Ajoutez vos fichiers en les déposant dans cette fenêtre
              </Text>
              <Text type="link" className={classes.textLink}>
                Ou parcourez vos documents
              </Text>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div {...rest} className={classes.fileUpload}>
      {!isReadOnly && (
        <div className={classes.dragContainer}>
          <div {...getRootProps({ className: classes.dropZone })}>
            <input {...getInputProps()} />
            <div>
              <img
                alt="ico-download"
                src={`${publicIconPath}/download.svg`}
                className={classes.imgUpload}
              />
              <Text className={classes.textMain}>Déposez vos fichiers</Text>
            </div>
            <Text className={classes.textAdd}>
              Ajoutez vos fichiers en les déposant dans cette fenêtre
            </Text>
            <Text type="link" className={classes.textLink}>
              Ou parcourez vos documents
            </Text>
          </div>
        </div>
      )}

      {!hideFileList && (
        <DefaultFilesList
          axis="xy"
          nbCols={nbCols}
          items={fileslist}
          onRemoveFile={onRemoveFile}
          isAdmin={isAdmin}
          isSplitted={isSplitted}
          mainLabel={mainLabel}
          mainEmptyLabel={mainEmptyLabel}
          secondaryLabel={secondaryLabel}
          secondaryItems={secondaryValue}
          secondaryEmptyLabel={secondaryEmptyLabel}
        />
      )}
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  fileUpload: {
    display: "flex",
    flexDirection: "row",
  },
  fileUploadVertical: {
    display: "flex",
    flexDirection: "column-reverse",
    flexGrow: 1,
  },
  divider: {
    width: 2,
    backgroundColor: "#DFE1E6",
    marginRight: 20,
    marginLeft: 20,
  },
  paperMargin: {
    marginBottom: 20,
    padding: 15,
  },
  paper: {
    composes: "$paperMargin",
    display: "flex",
    flexDirection: "column",
  },
  dragContainer: {
    border: `1px dashed ${theme.color.gray5}`,
    padding: [20, 5],
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  largeDragContainer: {
    border: `1px dashed ${theme.color.gray5}`,
    padding: [20, 5],
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  dropZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  textMain: {
    fontSize: 18,
  },
  imgUpload: {
    height: 20,
    marginRight: 10,
  },
  textAdd: {
    marginTop: 12,
    textAlign: "center",
  },
  textLink: {
    marginTop: 23,
    color: theme.color.gray3,
    textDecoration: "underline",
  },
  importSucces: {
    display: "flex",
    alignItems: "center",
    color: theme.color.green,
  },
  importError: {
    composes: "$importSucces",
    color: theme.color.red,
    marginTop: 10,
  },
  iconCHeck: {
    marginRight: 7,
  },
  ingestionMod: {
    composes: "$paperMargin",
    display: "flex",
    paddingLeft: 0,
    paddingRight: 0,
  },
  ingestionItems: {
    width: "33%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontSize: 12,
    color: theme.color.gray5,
    margin: 10,
    paddingLeft: 10,
    borderLeft: `1px solid ${theme.color.gray2}`,
  },
  btnIngest: {
    "&.MuiButtonBase-root": {
      marginBottom: 15,
    },
  },
  titleInfo: {
    margin: 0,
    marginLeft: 10,
  },
  ingestionMode: {
    marginTop: 20,
    marginBottom: 20,
  },
  bodyClassName: {
    display: "flex",
  },
  btnTemplateContainer: {
    display: "flex",
    justifyContent: "flex-end",
    flex: "1",
  },
  ingestStatusContainer: {
    display: "flex",
    flexDirection: "column",
  },
  ingestStatusItem: {
    marginTop: 5,
  },
  ingestBtn: {
    marginBottom: 5,
  },
}));

export default UploadFiles;
